'use client';

import { useEffect, useState, useMemo } from 'react';
import { SwiperSlide } from 'swiper/react';

import { AtCarousel } from '@/components/atoms/at-carousel/at-carousel';
import { MlCard } from '@/components/molecules/ml-card/ml-card';

import { EMlCardsTypes } from '@/components/molecules/ml-card/ml-card.types';
import { ETagVariants } from '@/components/atoms/at-tag/at-tag.types';

import { useLocation } from '@/context/locationContext';
import { getHomePlanUrl } from '@/components/helpers/formatters/home-plans';

import type {
  IHomePlanData,
  IOrHomePlansCarouselClientProps,
  THomePlansResponse,
} from './or-home-plans-carousel.types';
import { EThemes } from '@/lib/contentstack-sdk/types';
import Skeleton from 'react-loading-skeleton';
import {
  getImage,
  getFeatures,
  convertCmsPlansIntoAlgoliaPlans,
} from './utils';
import { sendGTMNavigationEvent } from '@/components/helpers/analytics/gtm';

export const OrHomePlansCarouselClient = ({
  planMetadata,
  likePlanAriaLabel,
  theme,
}: IOrHomePlansCarouselClientProps) => {
  const { currentBuildStudio } = useLocation();
  const [fullPlansResponse, setFullPlansResponse] =
    useState<THomePlansResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchPlans() {
      const plans = await convertCmsPlansIntoAlgoliaPlans(
        planMetadata,
        currentBuildStudio?.Id || 1,
      );

      setFullPlansResponse(plans);
      setLoading(false);
    }

    fetchPlans();

    // Remove SSR-only links for SEO
    const ssrLinks = document.getElementById('or-homeplans-ssr');
    if (ssrLinks) ssrLinks.remove();
  }, [planMetadata, currentBuildStudio, currentBuildStudio?.Id]);

  const fullPlans = useMemo(() => {
    return fullPlansResponse?.Plans || [];
  }, [fullPlansResponse?.Plans]);

  const slidesPerGroup = useMemo(() => {
    if (!fullPlans.length) return 0;
    return fullPlans.length % 3 === 0 ? 3 : 1;
  }, [fullPlans]);

  const handleHomePlanClick = (planName: string) => {
    const homePlanUrl = getHomePlanUrl(planName);
    sendGTMNavigationEvent(planName, 'or-home-plans-carousel', homePlanUrl);
  };

  if (loading)
    return (
      <div className="relative flex h-[626px] flex-row justify-center gap-4">
        <Skeleton
          containerClassName="w-1/3 block h-full inset-0 rounded-30"
          baseColor="var(--color-neutral-100)"
          highlightColor="var(--color-neutral-200)"
          className="inset-0 block h-full w-1/3 rounded-30"
          aria-hidden="true"
        />
        <Skeleton
          baseColor="var(--color-neutral-100)"
          highlightColor="var(--color-neutral-200)"
          containerClassName="w-1/3 block h-full inset-0 rounded-30"
          className="inset-0 block h-full w-1/3 rounded-30"
          aria-hidden="true"
        />
        <Skeleton
          baseColor="var(--color-neutral-100)"
          highlightColor="var(--color-neutral-200)"
          containerClassName="w-1/3 block h-full inset-0 rounded-30"
          className="inset-0 block h-full w-1/3 rounded-30"
          aria-hidden="true"
        />
      </div>
    );
  if (!fullPlans.length) return null;

  return (
    <AtCarousel
      buttonPosition="below"
      buttonTheme={theme === 'light' ? 'dark' : 'light'}
      controlClasses="lg:pr-8 xl:pr-15 2xl:pr-20"
      containerClasses="grid grid-cols-12"
      contentClasses="col-span-full"
      autoplay={false}
      slidesPerView={1.1}
      slidesPerGroup={1}
      spaceBetween={20}
      breakpoints={{
        800: {
          slidesPerGroup,
          slidesPerView: 3.05,
          spaceBetween: 0,
        },
        1770: {
          slidesPerGroup,
          slidesPerView: 3.0,
          spaceBetween: 0,
        },
      }}
      a11y={{
        prevSlideMessage: 'Previous slide',
        nextSlideMessage: 'Next slide',
      }}
    >
      {fullPlans.map(({ Plan }: { Plan: IHomePlanData }, i: number) => (
        <SwiperSlide
          key={`${i}_${Plan.Id}_${Plan.Name}`}
          className="md:pr-7 md:last:pr-0"
        >
          <MlCard
            className="relative mb-16 cursor-pointer"
            variant={EMlCardsTypes.HOME_PLAN}
            data-testid="or-home-plans-carousel-card"
            onClick={() => handleHomePlanClick(Plan.Name)}
            imageProps={getImage(Plan)}
            href={getHomePlanUrl(Plan.Name)}
            showFavouriteButton={false}
            theme={theme as EThemes}
            homePlanProps={{
              id: Plan.Id,
              name: Plan.Elevation?.Name || '',
              description: Plan.Elevation?.ExteriorStyle.Name || '',
              features: getFeatures(Plan, currentBuildStudio),
              likePlanAriaLabel,
              tags: [
                {
                  children: '+ Model Tour',
                  variant: ETagVariants.FEATURED,
                },
                {
                  children: `${Plan.exteriorCount === 1 ? '1 Exterior' : `${Plan.exteriorCount} Exteriors`}`,
                  variant: ETagVariants.DEFAULT,
                },
              ],
            }}
          />
        </SwiperSlide>
      ))}
    </AtCarousel>
  );
};
